import React, {
  ReactElement,
  useState,
  useEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import {
  BrowserRouter,
  Route,
  RouteComponentProps,
} from 'react-router-dom';
import {
  connectHits,
} from 'react-instantsearch-dom';
import axios from 'axios';
import { isEqual } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import {
  ORDER_ADMIN_PREFIX,
  ALGOLIA_ORDERS_INDEX,
  ORDER_ADMIN_VIEW_PREFIX,
  USER_ADMIN_PREFIX,
} from '../../../utils/constants';
import DateHelper from '../../../utils/DateHelper';
import AdminURLSync from '../AdminURLSync';
import { formatCurrency } from '../utils';

import useBulkSelected from '../../../utils/hooks/useBulkSelected';
import useModal from '../../../utils/hooks/useModal';
import ConfirmationModal from '../widgets/ConfirmationModal';
import DateFilterDisplay from '../widgets/filters/display-components/DateFilterDisplay';
import StatusFilterDisplay from '../widgets/filters/display-components/StatusFilterDisplay';
import BreedFilterDisplay from '../widgets/filters/display-components/BreedFilterDisplay';
import UserFilterDisplay from '../widgets/filters/display-components/UserFilterDisplay';
import { AlgoliaSimpleFacetOptions, OrderInterface, SearchState } from './interfaces';
import AdminDashboardTableHits from './AdminDashboardTableHits';
import AdminDashboardTableHit from './AdminDashboardTableHit';

const Hit: React.FC<OrderInterface> = ({
  id,
  created_at,
  user_id,
  user_email,
  user_name,
  total,
  status,
  selected,
  toggleSelected,
}) => {
  const [isHidden, setIsHidden] = useState(false);

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
  } = useModal();

  const updateModal = (type: string) => {
    switch (type) {
      case 'delete':
        setModalData({
          message: `Would you like to delete this order for ${user_name}: Order # ${id}?`,
          onConfirmAsync: () => {
            return new Promise((res, rej) => {
              axios.delete(`${ORDER_ADMIN_PREFIX}/${id}`)
                .then(({ data }) => {
                  res(data.message);
                })
                .catch((e) => {
                  // console.log(e);
                  rej(e);
                });
            });
          },
          onClose: () => {
            updateModal('');
          },
          onSuccess: () => {
            setIsHidden(true);
          },
        });
        break;
      default:
        setModalData({
          message: '',
          onClose: () => {},
        });
    }
  };

  // TODO: update `any`
  const values: any = [
    {
      key: 'delete-mobile',
      wrapperClass: 'd-xl-none',
      value: (
        <a
          href="#"
          className="mx-2"
          aria-label={`Delete order number ${id}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
    {
      key: 'date',
      value: (
        <p className="text-table-light-gray m-0">
          {DateHelper.getLocalizedDateTimeFromTimestamp(created_at).format('MM/DD/YY')}
        </p>
      ),
    },
    {
      key: 'order_number',
      value: (
        <a
          className="text-black m-0 font-weight-bold"
          href={`${ORDER_ADMIN_VIEW_PREFIX}/${id}`}
        >
          {id}
        </a>
      ),
    },
    {
      key: 'account',
      value: (
        <a
          className="text-black m-0"
          href={`${USER_ADMIN_PREFIX}/${user_id}`}
        >
          {user_name}
        </a>
      ),
    },
    {
      key: 'account_email',
      value: (
        <a
          className="text-table-light-gray m-0"
          href={`mailto:${user_email}`}
        >
          {user_email}
        </a>
      ),
    },
    {
      key: 'status',
      value: (
        <p className="text-table-light-gray m-0 text-capitalize">{status}</p>
      ),
    },
    {
      key: 'total',
      value: <p className="text-table-light-gray m-0">{`${formatCurrency(total, true)}`}</p>,
    },
    {
      key: 'delete',
      wrapperClass: 'd-none d-xl-table-cell',
      value: (
        <a
          href="#"
          className="widescreen mx-2"
          aria-label={`Delete order number ${id}.`}
          onClick={(e) => {
            e.preventDefault();
            updateModal('delete');
          }}
        >
          <FontAwesomeIcon icon={faTrashAlt} color="red" size="xs" />
        </a>
      ),
    },
  ];

  return (
    <>
      {!isHidden && (
        <AdminDashboardTableHit
          values={values}
          id={id}
          selected={selected}
          toggleSelected={toggleSelected}
        />
      )}
      {!isHidden && (
        <ConfirmationModal
          show={showModal}
          setShow={setShowModal}
          {...modalData}
        />
      )}
    </>
  );
};

const Hits: React.FC<{
  hits: any,
  selected: Array<number>,
  toggleSelected: (id: number) => void,
}> = ({ hits, selected, toggleSelected }) => {
  return (
    <AdminDashboardTableHits
      hits={hits}
      Hit={Hit}
      selected={selected}
      toggleSelected={toggleSelected}
    />
  );
};

const CustomHits = connectHits(Hits);

type Props = AlgoliaSimpleFacetOptions & RouteComponentProps;

const AdminOrders: React.FC<Props> = ({
  history,
  location,
  match,
  facets,
}): ReactElement => {
  const getAdType = (browserLocation) => {
    const { search } = browserLocation;
    const query = new URLSearchParams(search);
    return query.get('type') || 'all'; // Default to 'all'
  };

  const loadInitialSearchState = (adType) => {
    const facetFilters = (adType === 'puppy' || adType === 'stud')
      ? [`listings_type:${adType}`]
      : [];

    return {
      configure: {
        numericFilters: [],
        facetFilters,
      },
      query: '',
    };
  };

  const [adType, setAdType] = useState(getAdType(location));
  const [externalSearchState, setExternalSearchState] = useState<SearchState>(
    loadInitialSearchState(adType),
  );

  const {
    bulkAction,
    setBulkAction,
    updateBulkSelected,
    bulkSelected,
    toggleAllSelected,
    allSelected,
    setHits,
    clearBulkSelected,
  } = useBulkSelected();

  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
    modalFormState,
    setModalFormState,
    reloadPage,
  } = useModal();

  const performBulkAction = useCallback((action: string) => {
    if (bulkSelected.length < 1) {
      setModalData({
        message: 'No orders have been selected. Please, select one or more orders.',
        isSimple: true,
        onConfirm: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
        onClose: () => {
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
        },
      });
    } else {
      switch (action) {
        case 'delete':
          setModalData({
            message: `Would you like to delete ${bulkSelected.length} orders?`,
            onConfirmAsync: () => {
              return new Promise((res, rej) => {
                axios.delete(`${ORDER_ADMIN_PREFIX}/${bulkSelected.join(',')}`)
                  .then(({ data }) => {
                    res(data.message);
                  })
                  .catch((e) => {
                    // console.log(e);
                    rej(e);
                  });
              });
            },
            onClose: () => {
              performBulkAction('');
              setModalFormState({});
            },
            onSuccess: () => reloadPage(),
          });
          break;
        default:
          setModalData({
            message: '',
            onClose: () => {},
            isSimple: true,
          });
      }
    }
  }, [bulkSelected, modalFormState, reloadPage]);

  useEffect(() => {
    const { value } = bulkAction;
    if (value) {
      performBulkAction(value);
    }
  }, [bulkAction]);

  useEffect(() => {
    const type = getAdType(location);

    if (type !== adType) {
      setAdType(type);
    }
  }, [location]);

  useEffect(() => {
    // TODO: Determine whether we can set these per listing type
    if (adType === 'all') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, [])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: [],
            },
          });
        }
      }
    } else if (adType === 'puppy') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['listings_type:puppy'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['listings_type:puppy'],
            },
          });
        }
      }
    } else if (adType === 'stud') {
      const { configure } = externalSearchState;
      if (configure) {
        const { facetFilters } = configure;
        if (!isEqual(facetFilters, ['listings_type:stud'])) {
          setExternalSearchState({
            ...externalSearchState,
            configure: {
              ...externalSearchState.configure,
              facetFilters: ['listings_type:stud'],
            },
          });
        }
      }
    }
  }, [adType]);

  const tableHeaderRowValues = [
    {
      key: 'date',
      value: 'Date',
    },
    {
      key: 'order_number',
      value: 'Order #',
      sort: {
        desc: ALGOLIA_ORDERS_INDEX,
      },
    },
    {
      key: 'account',
      value: 'Account',
      sort: {
        asc: `${ALGOLIA_ORDERS_INDEX}_user_name_asc`,
      },
    },
    {
      key: 'account_email',
      value: 'Account Email',
      sort: {
        asc: `${ALGOLIA_ORDERS_INDEX}_account_email_asc`,
      },
    },
    {
      key: 'status',
      value: 'Status',
    },
    {
      key: 'total',
      value: 'total',
    },
  ];

  return (
    <>
      <AdminURLSync
        title="Orders"
        numEmptyStartHeaders={1}
        numEmptyEndHeaders={1}
        setBulkAction={setBulkAction}
        bulkAction={bulkAction}
        setHits={setHits}
        allSelected={allSelected}
        toggleAllSelected={toggleAllSelected}
        clearBulkSelected={clearBulkSelected}
        bulkActions={[
          {
            label: 'Delete',
            value: 'delete',
          },
        ]}
        externalQueryParams={[
          {
            key: 'type',
            value: adType,
          },
        ]}
        history={history}
        location={location}
        match={match}
        externalSearchState={externalSearchState}
        tableHeaderRowValues={tableHeaderRowValues}
        indexName={ALGOLIA_ORDERS_INDEX!}
        customHits={(
          <CustomHits
            selected={bulkSelected}
            toggleSelected={updateBulkSelected}
          />
        )}
        attributes={[
          {
            type: 'dateRange',
            attribute: 'created_at',
            startLabel: '',
            endLabel: '',
            DisplayComponent: <DateFilterDisplay />,
          },
          {
            type: 'facet',
            attribute: 'line_items.breed',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            DisplayComponent: <BreedFilterDisplay />,
            placeholder: '',
            simpleOptions: facets?.breed || [],
          },
          {
            type: 'facet',
            attribute: 'status',
            isSearchable: false,
            isAsync: false,
            isMulti: true,
            DisplayComponent: <StatusFilterDisplay />,
            placeholder: '',
            simpleOptions: facets?.status || [],
          },
          {
            type: 'facet',
            attribute: 'seller_name',
            label: 'Seller Profile',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <UserFilterDisplay title="Seller Profile:" />,
            placeholder: '',
          },
          {
            type: 'facet',
            attribute: 'contact_name',
            label: 'Contact Name',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <UserFilterDisplay title="Contact Name:" />,
            placeholder: '',
          },
          {
            type: 'facet',
            attribute: 'user_name',
            label: 'Account',
            isSearchable: true,
            isAsync: true,
            isMulti: true,
            isToggleable: true,
            DisplayComponent: <UserFilterDisplay title="Account:" />,
            placeholder: '',
          },
        ]}
        dateAttributes={[]}
        tabs={[
          {
            title: 'All Orders',
            route: `${ORDER_ADMIN_PREFIX}?type=all`,
            key: 'type=all',
            indexName: ALGOLIA_ORDERS_INDEX,
            facetFilters: [],
            numericFilters: [],
          },
          {
            title: 'Puppy Orders',
            route: `${ORDER_ADMIN_PREFIX}?type=puppy`,
            key: 'type=puppy',
            indexName: ALGOLIA_ORDERS_INDEX,
            facetFilters: ['listings_type:puppy'],
            numericFilters: [],
          },
          {
            title: 'Stud Orders',
            route: `${ORDER_ADMIN_PREFIX}?type=stud`,
            key: 'type=stud',
            indexName: ALGOLIA_ORDERS_INDEX,
            facetFilters: ['listings_type:stud'],
            numericFilters: [],
          },
        ]}
      />
      <ConfirmationModal
        show={showModal}
        setShow={setShowModal}
        modalFormState={modalFormState}
        clearBulkSelected={clearBulkSelected}
        {...modalData}
      />
    </>
  );
};

export default AdminOrders;

if (document.getElementById('react-admin-dashboard-table-orders')) {
  const element = document.getElementById('react-admin-dashboard-table-orders');
  const facets = element?.dataset.facets && JSON.parse(element?.dataset.facets);

  ReactDOM.render(
    <BrowserRouter>
      <Route
        path={ORDER_ADMIN_PREFIX}
        render={(routeProps) => (
          <AdminOrders
            facets={facets}
            {...routeProps}
          />
        )}
      />
    </BrowserRouter>,
    element,
  );
}
